/**
 * Admin Analytics dashboard.
 * Route: /admin/analytics
 *
 * Single fetch → entire dashboard. KPI cards + inline SVG sparklines
 * (no chart library — keeps the bundle slim and renders perfectly on
 * mobile). Non-admins are bounced to /dashboard with a toast.
 *
 * Future-proof: every "section" of the API response maps to its own
 * component below. Adding a new metric or chart means adding one card,
 * not refactoring the page.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, Users, Crown, Fish, Trophy, RefreshCw, Loader2, TrendingUp,
  AlertTriangle, Activity, CreditCard, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- helpers ---------------------------------------------------------------

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-GB') : '—');
const money = (curMap = {}) => {
  if (!curMap || Object.keys(curMap).length === 0) return '£0';
  return Object.entries(curMap).map(([c, v]) =>
    `${c === 'GBP' ? '£' : `${c} `}${(v.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  ).join(' · ');
};
const shortDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
  catch { return '—'; }
};

// ----- sparkline (inline SVG, no library) ------------------------------------

const Sparkline = ({ data, color = '#c8b273', height = 56 }) => {
  if (!data?.length) return <div className="h-14 bg-[#141714] rounded" />;
  const values = data.map((d) => d.value || 0);
  const max = Math.max(1, ...values);
  const w = 200;
  const h = height;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`).join(' ');
  const lastIdx = values.length - 1;
  const lastX = (lastIdx * step).toFixed(1);
  const lastY = (h - (values[lastIdx] / max) * (h - 4) - 2).toFixed(1);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-14">
      <defs>
        <linearGradient id={`fade-${color.replace('#', '')}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${points} ${w},${h}`} fill={`url(#fade-${color.replace('#', '')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
};

// ----- presentational --------------------------------------------------------

const KPI = ({ label, value, sub, icon, tone = 'gold', testId }) => {
  const toneClass = {
    gold:  'border-[#c8b273]/30 text-[#c8b273]',
    green: 'border-green-500/30 text-green-400',
    red:   'border-red-500/30 text-red-400',
    blue:  'border-blue-500/30 text-blue-400',
  }[tone] || 'border-[#c8b273]/30';
  return (
    <div className={`glass-card p-4 border ${toneClass}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8a948a]">{icon} {label}</div>
      <p className="text-white text-2xl md:text-3xl font-bold mt-1">{value}</p>
      {sub && <p className="text-[#8a948a] text-xs mt-1 truncate">{sub}</p>}
    </div>
  );
};

const TrendCard = ({ title, data, color, total }) => (
  <div className="glass-card p-4" data-testid={`trend-${title.replace(/\W+/g, '-').toLowerCase()}`}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs uppercase tracking-wider text-[#8a948a]">{title}</p>
      <p className="text-white text-sm font-bold">{fmt(total)}</p>
    </div>
    <Sparkline data={data} color={color} />
    <div className="flex justify-between text-[10px] text-[#4a504a] mt-1">
      <span>{shortDate(data?.[0]?.date)}</span>
      <span>{shortDate(data?.[data.length - 1]?.date)}</span>
    </div>
  </div>
);

const tierTone = (t) => ({
  elite: 'text-[#c8b273]', pro: 'text-blue-300', basic: 'text-[#8a948a]'
})[(t || 'basic').toLowerCase()] || 'text-[#8a948a]';

// ----- page ------------------------------------------------------------------

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const token = localStorage.getItem('carpcast_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendDays, setTrendDays] = useState(30);

  const load = async (days = trendDays) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics?trend_days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admins only');
        navigate('/dashboard', { replace: true });
        return;
      }
      toast.error('Could not load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(trendDays); /* eslint-disable-next-line */ }, [trendDays]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 text-[#c8b273] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const u = data.users, a = data.activity, c = data.content, ins = data.insights, p = data.payments, t = data.trends;
  const trendsSum = (arr) => (arr || []).reduce((s, x) => s + (x.value || 0), 0);

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12" data-testid="admin-analytics">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#c8b273] mb-1">
              <BarChart3 className="inline-block w-3 h-3 mr-1.5 -mt-0.5" /> Admin · Analytics
            </p>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">Platform overview</h1>
            <p className="text-[#8a948a] text-xs mt-1">Generated {new Date(data.generated_at).toLocaleTimeString('en-GB')}</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={trendDays}
              onChange={(e) => setTrendDays(Number(e.target.value))}
              className="bg-[#0b0d0b] border border-[#1f221f] rounded px-2 py-1.5 text-white text-xs"
              data-testid="analytics-trend-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={() => load(trendDays)}
              className="p-2 text-[#8a948a] hover:text-[#c8b273]"
              data-testid="analytics-refresh-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ROW 1: top-line KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="kpis-top">
          <KPI label="Total members" value={fmt(u.total)} sub={`${u.signups.day} today · ${u.signups.week} 7d · ${u.signups.month} 30d`} icon={<Users className="w-3.5 h-3.5" />} tone="gold" testId="kpi-total-members" />
          <KPI label="Elite members" value={fmt(u.tier_split.elite)} sub={`Pro ${u.tier_split.pro} · Basic ${u.tier_split.basic}`} icon={<Crown className="w-3.5 h-3.5" />} tone="gold" testId="kpi-elite" />
          <KPI label="Active subs" value={fmt(p.active_subscriptions)} sub={`${p.paid} paid · ${p.failed} failed`} icon={<CreditCard className="w-3.5 h-3.5" />} tone="green" testId="kpi-active-subs" />
          <KPI label="Revenue" value={money(p.revenue_by_currency)} sub={`${p.transactions_total} txns total`} icon={<Sparkles className="w-3.5 h-3.5" />} tone="gold" testId="kpi-revenue" />
        </div>

        {/* ROW 2: activity */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="kpis-activity">
          <KPI label="DAU" value={fmt(a.dau)} sub="Active in last 24h" icon={<Activity className="w-3.5 h-3.5" />} tone="green" testId="kpi-dau" />
          <KPI label="WAU" value={fmt(a.wau)} sub="Active in last 7d" icon={<Activity className="w-3.5 h-3.5" />} tone="green" testId="kpi-wau" />
          <KPI label="MAU" value={fmt(a.mau)} sub="Active in last 30d" icon={<Activity className="w-3.5 h-3.5" />} tone="green" testId="kpi-mau" />
          <KPI label="Suspended" value={fmt(u.suspended)} sub="Admin-actioned" icon={<AlertTriangle className="w-3.5 h-3.5" />} tone={u.suspended > 0 ? 'red' : 'gold'} testId="kpi-suspended" />
        </div>

        {/* ROW 3: content */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpis-content">
          <KPI label="Catches" value={fmt(c.catches_total)} sub={`${c.catches_day} today · ${c.catches_week} 7d`} icon={<Fish className="w-3.5 h-3.5" />} testId="kpi-catches" />
          <KPI label="Submissions" value={fmt(c.submissions_total)} sub={`${c.submissions_approved} approved · ${c.submissions_pending} pending`} icon={<Trophy className="w-3.5 h-3.5" />} testId="kpi-submissions" />
          <KPI label="Messages" value={fmt(c.messages_total)} sub="Direct messages" icon={<TrendingUp className="w-3.5 h-3.5" />} testId="kpi-messages" />
          <KPI label="Hidden catches" value={fmt(c.catches_total_incl_hidden - c.catches_total)} sub="Soft-moderated" icon={<AlertTriangle className="w-3.5 h-3.5" />} tone="gold" testId="kpi-hidden" />
        </div>

        {/* TRENDS */}
        <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">Trends · last {data.trend_days} days</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="trends-grid">
          <TrendCard title="Catches / day" data={t.catches_per_day} color="#c8b273" total={trendsSum(t.catches_per_day)} />
          <TrendCard title="Signups / day" data={t.signups_per_day} color="#4ade80" total={trendsSum(t.signups_per_day)} />
          <TrendCard title="Active users / day" data={t.active_users_per_day} color="#60a5fa" total={trendsSum(t.active_users_per_day)} />
          <TrendCard title="Competition entries / day" data={t.submissions_per_day} color="#f0b429" total={trendsSum(t.submissions_per_day)} />
        </div>

        {/* INSIGHTS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="insights-grid">
          <div className="glass-card p-4">
            <h3 className="text-xs uppercase tracking-wider text-[#8a948a] mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Newest members</h3>
            {ins.newest_users?.length ? (
              <ul className="space-y-2">
                {ins.newest_users.map((m) => (
                  <li key={m.id} className="flex justify-between text-sm">
                    <Link to={`/u/${m.id}`} className={`font-bold hover:underline ${tierTone(m.subscription_tier)}`}>{m.username}</Link>
                    <span className="text-[#8a948a] text-xs">{shortDate(m.created_at)}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-[#8a948a] text-sm">No members yet.</p>}
          </div>

          <div className="glass-card p-4">
            <h3 className="text-xs uppercase tracking-wider text-[#8a948a] mb-3 flex items-center gap-1.5"><Fish className="w-3.5 h-3.5" /> Most active anglers</h3>
            {ins.most_active_users?.length ? (
              <ul className="space-y-2">
                {ins.most_active_users.map((m) => (
                  <li key={m.user_id} className="flex justify-between text-sm">
                    <Link to={`/u/${m.user_id}`} className="text-white font-bold hover:text-[#c8b273] hover:underline truncate">{m.username || m.user_id.slice(0, 8)}</Link>
                    <span className="text-[#c8b273] font-bold">{m.catches}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-[#8a948a] text-sm">No catches yet.</p>}
          </div>

          <div className="glass-card p-4">
            <h3 className="text-xs uppercase tracking-wider text-[#8a948a] mb-3 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Biggest catch</h3>
            {ins.biggest_catch ? (
              <div className="space-y-1 text-sm">
                <p className="text-3xl font-bold text-[#c8b273]" data-testid="biggest-catch-weight">{ins.biggest_catch.weight}lb</p>
                <p className="text-white">{ins.biggest_catch.fish_type}</p>
                <Link to={`/u/${ins.biggest_catch.user_id}`} className="text-[#8a948a] text-xs hover:text-[#c8b273] block">by {ins.biggest_catch.username}</Link>
                {ins.biggest_catch.lake_name && <p className="text-[#6b7d6b] text-xs">{ins.biggest_catch.lake_name}</p>}
              </div>
            ) : <p className="text-[#8a948a] text-sm">No catches yet.</p>}
          </div>
        </div>

        {/* PAYMENTS DETAIL */}
        <div className="glass-card p-4 mb-6" data-testid="payments-card">
          <h3 className="text-xs uppercase tracking-wider text-[#8a948a] mb-3 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Payments</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><p className="text-[10px] uppercase tracking-wider text-[#8a948a]">Successful</p><p className="text-green-400 font-bold text-lg">{fmt(p.paid)}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#8a948a]">Failed / cancelled</p><p className="text-red-400 font-bold text-lg">{fmt(p.failed)}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#8a948a]">Active subs</p><p className="text-white font-bold text-lg">{fmt(p.active_subscriptions)}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#8a948a]">Revenue (paid)</p><p className="text-[#c8b273] font-bold text-lg truncate">{money(p.revenue_by_currency)}</p></div>
          </div>
        </div>

        <p className="text-center text-[10px] text-[#4a504a] uppercase tracking-wider">
          Aggregated server-side · zero PII · zero raw Stripe data
        </p>
      </div>
    </div>
  );
}
